import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_RD_BUSINESS_COMPONENTS_FILE,
  AddRdBusinessComponentsFileMutation,
  AddRdBusinessComponentsFileMutationVariables,
  GET_BACKGROUND_OPERATIONS,
  GET_ONE_SCHEMA_JWT,
  GET_RD_BUSINESS_COMPONENT_UPLOADS,
  GET_RD_BUSINESS_COMPONENTS_FILE_SIGNED_UPLOAD_URL,
  GetBackgroundOperationsQueryVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  GetRdBusinessComponentUploadsQueryVariables,
  GetRdBusinessComponentsFileSignedUploadUrlMutation,
  GetRdBusinessComponentsFileSignedUploadUrlMutationVariables,
  useCurrentUser,
} from '@neo.tax/shared';

type UseAddRdBusinessComponentsFileParams = {
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
};

type UseAddRdBusinessComponentsFileReturn = {
  handleSuccess: () => Promise<void>;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

const BUSINESS_COMPONENTS_FILE_NAME = 'rd-business-components.csv';

export const useAddRdBusinessComponentsFile = ({
  companyId,
  fiscalYearId,
  isOpen,
}: UseAddRdBusinessComponentsFileParams): UseAddRdBusinessComponentsFileReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData }] = useMutation<
    GetRdBusinessComponentsFileSignedUploadUrlMutation,
    GetRdBusinessComponentsFileSignedUploadUrlMutationVariables
  >(GET_RD_BUSINESS_COMPONENTS_FILE_SIGNED_UPLOAD_URL);
  const [addRdBusinessComponentsFile] = useMutation<
    AddRdBusinessComponentsFileMutation,
    AddRdBusinessComponentsFileMutationVariables
  >(ADD_RD_BUSINESS_COMPONENTS_FILE);

  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user?.id) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: { title: 'Error preparing business components upload' },
        },
      });
    };
    initOneSchemaJwt();
  }, [getOneSchemaJwt, isOpen, user?.id]);

  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen) {
        return;
      }
      await getUploadUrlMutation({
        variables: { fiscalYearId, fileName: BUSINESS_COMPONENTS_FILE_NAME },
        context: {
          errorToast: { title: 'Error preparing business components upload' },
        },
      });
    };
    initUploadUrls();
  }, [fiscalYearId, getUploadUrlMutation, isOpen]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getRdBusinessComponentsFileSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }
    await addRdBusinessComponentsFile({
      variables: { fiscalYearId, input: { permanentUrl } },
      refetchQueries: [
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
        {
          query: GET_RD_BUSINESS_COMPONENT_UPLOADS,
          variables: { fiscalYearId } satisfies GetRdBusinessComponentUploadsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'Business components upload started!',
          description:
            "We're importing your business components. They'll appear on this page once processing completes.",
        },
        errorToast: { title: 'Failed to upload business components' },
      },
    });
  }, [addRdBusinessComponentsFile, companyId, fiscalYearId, uploadUrlData]);

  return {
    handleSuccess,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getRdBusinessComponentsFileSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getRdBusinessComponentsFileSignedUploadUrl.signedUrl ?? null,
  };
};
